/* ============================================================
   SINGSPACE - UTILITAIRES PARTAGÉS
   ============================================================ */

import { CURRENCY_SYMBOL } from '../constants/index.js';

// ============================================================
// FORMATAGE DES MONTANTS
// ============================================================

/**
 * Formate un montant en FCFA
 * @param amount Montant en entier (pas de centimes pour XAF)
 * @param options Options de formatage
 */
export function formatAmount(amount: number, options: { showCurrency?: boolean; compact?: boolean } = {}): string {
  const { showCurrency = true, compact = false } = options;

  if (compact && amount >= 1000000) {
    const millions = amount / 1000000;
    const formatted = millions.toFixed(millions % 1 === 0 ? 0 : 1);
    return `${formatted}M${showCurrency ? ` ${CURRENCY_SYMBOL}` : ''}`;
  }

  if (compact && amount >= 1000) {
    const thousands = amount / 1000;
    const formatted = thousands.toFixed(thousands % 1 === 0 ? 0 : 1);
    return `${formatted}k${showCurrency ? ` ${CURRENCY_SYMBOL}` : ''}`;
  }

  const formatted = new Intl.NumberFormat('fr-FR').format(amount);
  return showCurrency ? `${formatted} ${CURRENCY_SYMBOL}` : formatted;
}

/**
 * Parse un montant depuis une chaîne formatée
 */
export function parseAmount(value: string): number {
  const cleaned = value.replace(/[^\d]/g, '');
  return parseInt(cleaned, 10) || 0;
}

// ============================================================
// FORMATAGE DES DATES
// ============================================================

/**
 * Formate une date en français
 */
export function formatDate(date: Date | string, format: 'short' | 'long' | 'datetime' | 'time' = 'short'): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  const options: Intl.DateTimeFormatOptions = (() => {
    switch (format) {
      case 'long':
        return { day: 'numeric', month: 'long', year: 'numeric' };
      case 'datetime':
        return { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
      case 'time':
        return { hour: '2-digit', minute: '2-digit' };
      default:
        return { day: '2-digit', month: '2-digit', year: 'numeric' };
    }
  })();

  return new Intl.DateTimeFormat('fr-FR', options).format(d);
}

/**
 * Formate une date relative (il y a X jours, etc.)
 */
export function formatRelativeDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      if (diffMinutes < 5) return "À l'instant";
      return `Il y a ${diffMinutes} min`;
    }
    return `Il y a ${diffHours}h`;
  }

  if (diffDays === 1) return 'Hier';
  if (diffDays < 7) return `Il y a ${diffDays} jours`;
  if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaine(s)`;
  if (diffDays < 365) return `Il y a ${Math.floor(diffDays / 30)} mois`;

  return formatDate(d, 'long');
}

// ============================================================
// GÉNÉRATION D'IDENTIFIANTS
// ============================================================

/**
 * Génère une référence unique pour les réservations
 * Format: SING-YYYYMMDD-XXXX
 */
export function generateReference(prefix = 'SING'): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();

  return `${prefix}-${year}${month}${day}-${random}`;
}

/**
 * Génère un slug à partir d'un texte
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
    .replace(/[^a-z0-9\s-]/g, '') // Supprimer les caractères spéciaux
    .replace(/\s+/g, '-') // Remplacer les espaces par des tirets
    .replace(/-+/g, '-') // Supprimer les tirets multiples
    .replace(/^-|-$/g, ''); // Supprimer les tirets au début/fin
}

// ============================================================
// VALIDATION
// ============================================================

/**
 * Valide un numéro de téléphone gabonais
 */
export function isValidGabonPhone(phone: string): boolean {
  const cleaned = phone.replace(/\s/g, '');
  return /^(\+241|00241)?[0-9]{8}$/.test(cleaned);
}

/**
 * Valide une adresse email
 */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Formate un numéro de téléphone gabonais
 * Entrée: 074123456 ou +241074123456
 * Sortie: +241 074 12 34 56
 */
export function formatGabonPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  let digits = cleaned;

  // Retirer le préfixe s'il existe
  if (digits.startsWith('241')) {
    digits = digits.slice(3);
  } else if (digits.startsWith('00241')) {
    digits = digits.slice(5);
  }

  // Formater: XXX XX XX XX
  if (digits.length === 8 || digits.length === 9) {
    const prefix = digits.slice(0, digits.length - 6);
    const part1 = digits.slice(-6, -4);
    const part2 = digits.slice(-4, -2);
    const part3 = digits.slice(-2);
    return `+241 ${prefix} ${part1} ${part2} ${part3}`;
  }

  return phone; // Retourner tel quel si format non reconnu
}

// ============================================================
// STRINGS
// ============================================================

/**
 * Tronque un texte avec ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}

/**
 * Met en majuscule la première lettre
 */
export function capitalize(text: string): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Récupère les initiales d'un nom
 */
export function getInitials(name: string, maxChars = 2): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, maxChars)
    .join('');
}

// ============================================================
// OBJETS
// ============================================================

/**
 * Supprime les clés undefined/null d'un objet
 */
export function cleanObject<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const key in obj) {
    if (obj[key] !== undefined && obj[key] !== null) {
      result[key] = obj[key];
    }
  }
  return result;
}

/**
 * Deep clone d'un objet
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// ============================================================
// DEBOUNCE / THROTTLE
// ============================================================

/**
 * Debounce une fonction
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

// ============================================================
// CLASSNAMES HELPER
// ============================================================

type ClassValue = string | boolean | undefined | null | { [key: string]: boolean };

/**
 * Combine des classes CSS conditionnellement
 * Usage: cn('base', condition && 'active', { 'error': hasError })
 */
export function cn(...args: ClassValue[]): string {
  const classes: string[] = [];

  for (const arg of args) {
    if (!arg) continue;

    if (typeof arg === 'string') {
      classes.push(arg);
    } else if (typeof arg === 'object') {
      for (const key in arg) {
        if (arg[key]) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(' ');
}

// ============================================================
// STORAGE HELPERS
// ============================================================

/**
 * Récupère une valeur du localStorage avec parsing JSON
 */
export function getStorageItem<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Enregistre une valeur dans localStorage
 */
export function setStorageItem<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('Failed to save to localStorage:', e);
  }
}
